import { useEffect, useRef } from 'react';
import { useHeatmapStore, type HeatmapEnvelope } from '../store/heatmapStore';
import { startClientTiming } from '../lib/timing';

type HeatmapEventSource = 'bootstrap' | 'live';

interface HeatmapStreamEnvelope extends HeatmapEnvelope {
  source?: HeatmapEventSource;
}

interface BootstrapStatusEnvelope {
  status: 'complete' | 'failed';
  heatmapCount: number;
}

function parseJson<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch (err) {
    console.warn('[heatmapStream] failed to parse SSE payload', err);
    return null;
  }
}

function isBootstrapStatusEnvelope(value: unknown): value is BootstrapStatusEnvelope {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const envelope = value as Partial<BootstrapStatusEnvelope>;
  return (
    (envelope.status === 'complete' || envelope.status === 'failed') &&
    typeof envelope.heatmapCount === 'number'
  );
}

export function useHeatmapStream(
  enabled: boolean,
  symbols: readonly string[],
  onSymbolCatalogRefreshRequested?: () => Promise<unknown> | void,
  onBootstrapSettled?: (symbols: readonly string[]) => void,
) {
  const upsertHeatmap = useHeatmapStore((state) => state.upsertHeatmap);
  const setConnectionStatus = useHeatmapStore((state) => state.setConnectionStatus);
  const setBootstrapStatus = useHeatmapStore((state) => state.setBootstrapStatus);
  const catalogRefreshInFlightRef = useRef(false);
  const catalogRefreshPendingRef = useRef(false);
  const onSymbolCatalogRefreshRequestedRef = useRef(onSymbolCatalogRefreshRequested);
  const onBootstrapSettledRef = useRef(onBootstrapSettled);

  useEffect(() => {
    onSymbolCatalogRefreshRequestedRef.current = onSymbolCatalogRefreshRequested;
  }, [onSymbolCatalogRefreshRequested]);

  useEffect(() => {
    onBootstrapSettledRef.current = onBootstrapSettled;
  }, [onBootstrapSettled]);

  useEffect(() => {
    if (!enabled || symbols.length === 0) {
      return;
    }

    const query = new URLSearchParams({ symbols: symbols.join(',') });
    const endOpenTiming = startClientTiming('ui/sse-open');
    const endFirstHeatmapTiming = startClientTiming('ui/sse-first-heatmap');
    let receivedFirstHeatmap = false;
    const eventSource = new EventSource(`/api/stream?${query.toString()}`);
    setConnectionStatus('connecting');
    setBootstrapStatus('pending');

    const handleOpen = () => {
      endOpenTiming();
      setConnectionStatus('connected');
      setBootstrapStatus('pending');
    };

    const handleHeatmap = (event: MessageEvent<string>) => {
      const payload = parseJson<HeatmapStreamEnvelope>(event.data);
      if (!payload) {
        return;
      }

      const heatmap = { ...payload };
      delete heatmap.source;
      upsertHeatmap(heatmap);
      if (!receivedFirstHeatmap) {
        receivedFirstHeatmap = true;
        endFirstHeatmapTiming();
      }
    };

    const handleBootstrapStatus = (event: MessageEvent<string>) => {
      const payload = parseJson<BootstrapStatusEnvelope>(event.data);
      if (!isBootstrapStatusEnvelope(payload)) {
        console.warn('[heatmapStream] invalid bootstrap status payload');
        return;
      }

      setBootstrapStatus(payload.status, payload.heatmapCount);
      onBootstrapSettledRef.current?.(symbols);
    };

    const requestCatalogRefresh = async () => {
      if (onSymbolCatalogRefreshRequestedRef.current == null) {
        return;
      }

      if (catalogRefreshInFlightRef.current) {
        catalogRefreshPendingRef.current = true;
        return;
      }

      catalogRefreshInFlightRef.current = true;
      try {
        do {
          catalogRefreshPendingRef.current = false;
          const refreshCatalog = onSymbolCatalogRefreshRequestedRef.current;
          if (refreshCatalog == null) {
            return;
          }
          await refreshCatalog();
        } while (catalogRefreshPendingRef.current);
      } finally {
        catalogRefreshInFlightRef.current = false;
      }
    };

    const handleSymbolsChanged = () => {
      void requestCatalogRefresh();
    };

    const handleError = () => {
      setConnectionStatus('retrying');
      void requestCatalogRefresh();
    };

    eventSource.addEventListener('open', handleOpen);
    eventSource.addEventListener('heatmap', handleHeatmap as EventListener);
    eventSource.addEventListener('bootstrap-status', handleBootstrapStatus as EventListener);
    eventSource.addEventListener('symbols-changed', handleSymbolsChanged as EventListener);
    eventSource.addEventListener('error', handleError as EventListener);

    return () => {
      endOpenTiming();
      endFirstHeatmapTiming();
      eventSource.close();
    };
  }, [enabled, setBootstrapStatus, setConnectionStatus, symbols, upsertHeatmap]);
}
